import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { SystemIssue, SystemIssueSource, SystemIssueStatus } from "@backstory/protocol";
import { ErrorState } from "../../components/States";
import { AutoLoadMore, PageHeader, RowMenu, SearchSelect } from "../../components/ui";
import { usePlatformAiCapability } from "./platformAi";
import "../ai/ai.css";
import "./staff.css";
import { formatDateTime, formatRelative } from "../../lib/format";
import { staffApi, staffKeys } from "./api";
import { CustomerSelect, useDebounced } from "./customers";
import { AcknowledgeDialog, IssueEnvironment, IssueScope, SeverityBadge, sourceLabels, useSystemIssueActions } from "./systemIssues";

const PAGE_SIZE = 50;

const statusOptions = [
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "all", label: "Open and acknowledged" },
];

const sourceOptions = [
  { value: "", label: "All sources" },
  { value: "background_jobs", label: "Background jobs" },
  { value: "ai", label: "AI" },
  { value: "deep_analysis", label: "AI analyses" },
  { value: "ai_health", label: "AI route health" },
  { value: "exports", label: "Session exports" },
  { value: "warehouse", label: "Warehouse exports" },
  { value: "notifications", label: "Notifications" },
  { value: "assets", label: "Asset archival" },
  { value: "billing", label: "Billing" },
];

const rangeOptions = [
  { value: "24", label: "Last 24 hours" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
  { value: "0", label: "All retained history" },
];

/**
 * Deployment-wide operational failures for SaaS staff, as a work queue rather
 * than a log: identical failures arrive already grouped, the queue shows what
 * still needs a decision, and everything about one problem lives on its own
 * page. The endpoint reads the durable worker-owned stores directly, so
 * refreshing cannot lose an incident.
 */
export function StaffSystemIssuesPage() {
  const [status, setStatus] = useState<SystemIssueStatus | "all">("open");
  const [source, setSource] = useState<SystemIssueSource | "">("");
  const [orgId, setOrgId] = useState("");
  const [sinceHours, setSinceHours] = useState(168);
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput.trim(), 250);
  const filters = useMemo(
    () => ({ source: source || undefined, status, sinceHours, q: q || undefined, orgId: orgId || undefined }),
    [source, status, sinceHours, q, orgId],
  );
  const filtered = !!(q || source || orgId);

  const issues = useInfiniteQuery({
    queryKey: staffKeys.systemIssues(filters),
    queryFn: ({ pageParam }) => staffApi.systemIssues({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return last.items.length === PAGE_SIZE && loaded < last.total ? loaded : undefined;
    },
    // A queued investigation belongs to the backend, so leaving or refreshing is
    // safe. While one is active this list polls its durable state; otherwise it
    // returns to the inexpensive operational-log refresh.
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.items.some((issue) => issue.investigation?.state === "pending")) ? 1_500 : 30_000,
  });
  const summary = issues.data?.pages[0];
  const rows = issues.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      <PageHeader
        title="System issues"
        subtitle="Recurring failures recorded by platform jobs and integrations."
        actions={
          <button className="btn ghost" type="button" onClick={() => void issues.refetch()} disabled={issues.isFetching}>
            {issues.isFetching && !issues.isFetchingNextPage ? "Refreshing…" : "Refresh"}
          </button>
        }
      >
        {/* Inside the head, so the filters stay reachable while a long queue
            scrolls and the column headers pin beneath them. */}
        <div className="toolbar customers-filters" aria-label="System issue filters">
          <input
            className="input customers-search"
            type="search"
            placeholder="Search issues, details, or customers"
            aria-label="Search system issues"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          <SearchSelect
            value={status}
            options={statusOptions}
            onChange={(value) => setStatus(value as SystemIssueStatus | "all")}
            aria-label="Status"
            searchPlaceholder="Search statuses…"
          />
          <SearchSelect
            value={source}
            options={sourceOptions}
            onChange={(value) => setSource(value as SystemIssueSource | "")}
            aria-label="Source"
            searchPlaceholder="Search sources…"
          />
          <CustomerSelect value={orgId} onChange={setOrgId} />
          <SearchSelect
            value={String(sinceHours)}
            options={rangeOptions}
            onChange={(value) => setSinceHours(Number(value))}
            aria-label="Time range"
            searchPlaceholder="Search time ranges…"
          />
          {summary && (
            <span className="muted small">
              {summary.total.toLocaleString()} {summary.total === 1 ? "issue" : "issues"} · {summary.occurrences.toLocaleString()} failures
            </span>
          )}
        </div>
      </PageHeader>

      {issues.isPending ? (
        <div className="table-scroll stack-table sys-issues-table">
          <table className="table" aria-busy="true">
            <tbody>
              <IssueSkeletonRows count={4} />
            </tbody>
          </table>
        </div>
      ) : issues.isError ? (
        <ErrorState error={issues.error} retry={() => void issues.refetch()} />
      ) : rows.length === 0 ? (
        <div className="card empty">
          <h3>{filtered ? "Nothing matches" : status === "open" ? "Nothing needs attention" : "No system issues"}</h3>
          {filtered ? (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setQInput("");
                setSource("");
                setOrgId("");
              }}
            >
              Clear filters
            </button>
          ) : (
            <p>
              {status === "open"
                ? "No unacknowledged failures were recorded for this time range."
                : "No failures were recorded for this time range."}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="table-scroll stack-table sys-issues-table">
            <table className="table" data-testid="system-issues-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Issue</th>
                  <th>Failures</th>
                  <th>Customer</th>
                  <th>Environment</th>
                  <th>Last seen</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((issue) => (
                  <IssueRow key={issue.id} issue={issue} />
                ))}
                {issues.isFetchingNextPage && <IssueSkeletonRows count={3} />}
              </tbody>
            </table>
          </div>
          <AutoLoadMore
            hasMore={!!issues.hasNextPage}
            loading={issues.isFetchingNextPage}
            loadMore={() => void issues.fetchNextPage()}
            label="Loading more issues"
          />
        </>
      )}
    </div>
  );
}

function IssueSkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={`skeleton-${i}`} className="sys-issue-skeleton" aria-hidden="true">
          <td>
            <span className="skeleton" style={{ width: 48 }}>loading</span>
          </td>
          <td className="sys-issue-title-cell">
            <span className="skeleton" style={{ width: `${55 + ((i * 13) % 30)}%` }}>loading</span>
            <span className="skeleton" style={{ width: 120, display: "block", marginTop: 6 }}>loading</span>
          </td>
          <td>
            <span className="skeleton" style={{ width: 40 }}>loading</span>
          </td>
          <td>
            <span className="skeleton" style={{ width: 96 }}>loading</span>
          </td>
          <td>
            <span className="skeleton" style={{ width: 56 }}>loading</span>
          </td>
          <td>
            <span className="skeleton" style={{ width: 72 }}>loading</span>
          </td>
          <td />
        </tr>
      ))}
    </>
  );
}

function IssueRow({ issue }: { issue: SystemIssue }) {
  const navigate = useNavigate();
  const ai = usePlatformAiCapability();
  const { investigate, reopen } = useSystemIssueActions(issue.id);
  const [acknowledging, setAcknowledging] = useState(false);
  const pending = issue.investigation?.state === "pending" || investigate.isPending;
  const open = () => void navigate({ to: "/staff/system/$id", params: { id: issue.id } });

  return (
    <tr onClick={open} data-testid={`system-issue-${issue.id}`}>
      <td className="sys-issue-severity-cell" data-label="Severity">
        <SeverityBadge severity={issue.severity} />
      </td>
      <td className="sys-issue-title-cell" data-label="Issue">
        <div className="sys-issue-title">{issue.title}</div>
        <div className="sys-issue-meta">
          <span>{sourceLabels[issue.source]}</span>
          {issue.status === "acknowledged" && <span className="badge neutral">Acknowledged</span>}
          {pending && <span className="badge info">Investigating</span>}
          {issue.investigation?.state === "done" && <span className="badge ai">Investigated</span>}
        </div>
      </td>
      <td data-label="Failures">
        <div className="sys-issue-count">{issue.occurrences.toLocaleString()}</div>
        <div className="muted small sys-issue-since">since {formatRelative(issue.firstSeen)}</div>
      </td>
      <td data-label="Customer">
        <IssueScope issue={issue} />
      </td>
      <td data-label="Environment">
        <IssueEnvironment issue={issue} />
      </td>
      <td className="small sys-issue-seen-cell" data-label="Last seen" title={formatDateTime(issue.lastSeen)}>
        {formatRelative(issue.lastSeen)}
      </td>
      {/* The menu is inside a clickable row, so its own clicks must not also
          count as opening the issue. */}
      <td className="sys-issue-actions-cell" onClick={(e) => e.stopPropagation()}>
        <RowMenu label={`Actions for ${issue.title}`} testId={`system-issue-menu-${issue.id}`}>
          <button type="button" role="menuitem" onClick={open}>
            View details
          </button>
          {/* A disabled menu item cannot carry a popover, so when AI is not
              configured the item says so instead of going quiet. */}
          <button type="button" role="menuitem" disabled={ai.blocked || pending} onClick={() => investigate.mutate()}>
            {ai.blocked
              ? `Investigate — ${ai.hint.toLowerCase()}`
              : pending
                ? "Investigating…"
                : issue.investigation?.result
                  ? "Re-investigate"
                  : "Investigate"}
          </button>
          {issue.status === "acknowledged" ? (
            <button type="button" role="menuitem" onClick={() => reopen.mutate()}>
              Reopen
            </button>
          ) : (
            <button type="button" role="menuitem" onClick={() => setAcknowledging(true)}>
              Acknowledge…
            </button>
          )}
        </RowMenu>
        <AcknowledgeDialog issue={issue} open={acknowledging} onClose={() => setAcknowledging(false)} />
      </td>
    </tr>
  );
}
